---
title: "Как сжать PDF до 5 МБ для Госуслуг"
description: "PDF не загружается в Госуслуги из-за лимита по размеру? Вот как уменьшить файл до 5 МБ без Acrobat, что реально сжимается и что делать, если файл не уменьшается."
locale: "ru"
date: "2026-05-16"
tags: ["pdf", "сжатие", "госуслуги"]
relatedTools: ["szhat"]
translations:
  en: "how-to-compress-pdf-to-5-mb"
faq:
  - q: "Можно ли точно получить файл меньше 5 МБ с первого раза?"
    a: "Не всегда. Если PDF состоит из живого текста и векторной графики, он почти не сжимается. Если внутри сканы или фото, шанс высокий. Практически это выглядит так: сначала пробуйте стандартное локальное сжатие, потом - более сильный режим."
  - q: "Файл уйдёт на сервер?"
    a: "Нет. Инструмент сжимает PDF прямо в браузере, файл остаётся на вашем устройстве."
  - q: "Почему после сжатия размер почти не меняется?"
    a: "Так бывает с PDF, который уже собран из текста, таблиц и векторных объектов без тяжёлых картинок. Там нечего заметно пережимать: документ и так компактный."
  - q: "Качество сильно просядет?"
    a: "На сканах и фотографиях зависит от выбранного режима. Обычно текст остаётся читаемым, а выигрыш по размеру заметен сразу. Если документ нужен для архива или печати, проверьте мелкий шрифт на итоговом файле перед загрузкой."
---

import { SzhatForm } from '@/pages/szhat/SzhatForm'

Форма на Госуслугах ругается на размер файла. PDF весит 8,7 МБ, лимит - 5 МБ,
и узнаёшь ты это, конечно, в самом конце, когда уже всё заполнил. Ради такого
сценария люди зачем-то открывают "печать в PDF", пересохраняют документ три
раза подряд и надеются на чудо. Обычно чудо не происходит.

Ниже встроен сжиматель. Кидаете файл, смотрите, сколько удалось снять. Если
повезло - вопрос закрыт за минуту. Если не повезло, там же есть более жёсткий
режим.

<div className="my-8 rounded-xl border bg-card p-5 shadow-sm">
  <SzhatForm />
</div>

## Откуда вообще берутся эти лишние мегабайты

Если PDF родился из Word или Google Docs, с ним обычно скучно. Там текст,
шрифты, векторные линии, немного графики. Такие файлы и так часто весят
смешные 400-800 КБ, и выжимать из них ещё что-то почти бессмысленно.

Вес набирают сканы. Особенно цветные. Особенно с МФУ, у которого по умолчанию
всё выставлено "побогаче". Снаружи это один PDF, внутри - набор картинок.
Иногда очень тяжёлых. Печати, серый фон, мусор от сканера, криво снятый лист,
фото вместо документа - всё это потом оплачивается мегабайтами.

## Что здесь происходит при сжатии

По умолчанию файл жмётся локально, прямо в браузере. Это нормальный первый
проход, а не демо-режим "попробуйте бесплатно". Для обычного скана его часто
хватает.

Но не каждый PDF хочет сотрудничать. Бывает старый факсовый скан, бывает
какая-нибудь экзотика внутри, бывает просто тяжёлый документ, который уже один
раз кто-то неудачно пережал до вас. В таких случаях локальный режим может
скинуть совсем мало. Тогда попробуйте сильный уровень, а если не помогло,
уберите лишние страницы или экспортируйте документ заново с меньшим
качеством картинок.

И вот это место лучше не замыливать маркетингом. Smallpdf, iLovePDF, PDF24 -
у них файл обычно уезжает сразу. Здесь нет: всё происходит в браузере. Разница
не философская, а вполне бытовая: одно
дело справка для кружка, другое - паспорт, договор или доверенность.

## Как быстрее попасть в лимит 5 МБ

Если после первого прохода файл стал 5,9 МБ, это ещё не повод идти
пересканировать весь пакет. Обычно в этот момент и нужен второй, более жёсткий
проход. Хуже, когда у вас 23 страницы цветного скана, на каждой печать, подпись
и серый фон "как есть". Тут никакая магия не обещана. Иногда быстрее реально
сделать новый скан в 150-200 dpi и без цвета.

Если размер почти не изменился, тоже не надо искать баг. Скорее всего, PDF и
так был компактный. Текст, таблицы, немного вектора - там просто нечего жать.
Лучше проверить, не прикладываете ли вы лишнее. Пустые страницы люди носят в
госпорталы с удивительным упорством.

Рабочая схема без героизма такая: сначала стандартный уровень, потом при
необходимости сильный, потом открыть итоговый PDF и посмотреть на мелкий текст. Если
после этого файл стал меньше 5 МБ, спор с формой обычно заканчивается.
